import {AppActions, useAppContext} from "../context/AppContext";
import {Table, Tag, Typography} from "antd";
import React, {useEffect} from "react";
import AppColor from "../components/AppColor";
import {findComparisonSide} from "../helpers/SmartPlanSelection";

export default function SelectPlan({type, smartCompare = false}) {
    const {appContext, selection, dispatchProject, resolveSelectionSide} = useAppContext()
    const resolvedSide = resolveSelectionSide(selection?.[type]) || {}
    const resolvedProject = resolvedSide.project
    const resolvedPlan = resolvedSide.plan

    // Selecting a base plan also selects the plan it is normally reviewed
    // against (Effective -> Duplicate Effective -> Corrected Effective ->
    // Revised), when such a plan is available.
    const withSmartCompare = (_selection, {project, plan}) => {
        if(!smartCompare || type !== 'base')
            return _selection

        const compare = findComparisonSide({
            projects: appContext?.projects,
            baseProjectId: project?.id,
            basePlan: plan,
            baseRiver: _selection?.base?.river,
        })

        if(!compare)
            return _selection

        return {..._selection, compare}
    }

    const loadProject = (id) => {
        const project = appContext?.projects?.find(x => x?.id === id)
        if(project) {
            const plan = project?.projectFiles[0]
            const surveys = project?.shapeFiles?.filter(x => x.hasSurveyData)
            const _selection = {
                ...selection,
                [type]: {
                    projectId: project?.id,
                    planId: plan?.id,
                    river: plan?.rivers ? plan.rivers[0] : undefined,
                    survey: {...(surveys?.length > 0 ? surveys[0] : {}),}
                }
            }

            dispatchProject({
                type: AppActions.SET_PROJECT_SELECTION,
                data: {
                    selection: withSmartCompare(_selection, {project, plan})
                }
            })
        }
    }

    return (
        <div style={{display: 'flex', alignItems: 'start', gap: 8}}>
            <Table
                pagination={false}
                style={{width: 400}}
                size={'small'}
                dataSource={appContext?.projects}
                rowKey={'id'}
                columns={[
                    {
                        key: '1',
                        dataIndex: '2',
                        title: 'Projects',
                        render: (text, record) => (
                            <Typography.Link
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    color: resolvedProject?.id === record?.id ? AppColor.mainColor : 'black',
                                    fontWeight: resolvedProject?.id === record?.id ? 'bold' : '',
                                    cursor: 'pointer'
                                }}
                                onClick={() => loadProject(record?.id)}
                            >
                                <div style={{minWidth:300}}>{record?.information?.title}{resolvedProject?.id === record?.id ? <>&#x2022;</> : ''}</div> <Tag>{record?.modelType || 'Model 1'}</Tag>
                            </Typography.Link>
                        )
                    }
                ]}
            />
            <Table
                pagination={false}
                style={{width: 300}}
                size={'small'}
                dataSource={resolvedProject?.projectFiles || []}
                rowKey={'id'}
                columns={[
                    {
                        key: '1',
                        dataIndex: '2',
                        title: `Plans: ${resolvedProject?.information?.title}`,
                        render: (text, record) => (
                            <>
                                <Typography.Link
                                    onClick={() => {
                                        const _selection = {
                                            ...selection,
                                            [type]: {
                                                ...selection[type],
                                                projectId: resolvedProject?.id,
                                                planId: record?.id,
                                                river: record?.rivers ? record.rivers[0] : undefined,
                                            }
                                        }

                                        dispatchProject({
                                            type: AppActions.SET_PROJECT_SELECTION,
                                            data: {
                                                selection: withSmartCompare(_selection, {
                                                    project: resolvedProject,
                                                    plan: record,
                                                })
                                            }
                                        })
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        color: resolvedPlan?.id === record?.id ? AppColor.mainColor : 'black',
                                        fontWeight: resolvedPlan?.id === record?.id ? 'bold' : '',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {record?.type} {resolvedPlan?.id === record?.id ? <>&#x2022;</> : ''}
                                </Typography.Link>
                                <span style={{color: 'black'}}>{record?.plan?.fileName}</span>
                            </>
                        )
                    }
                ]}
            />
            {
                resolvedPlan?.rivers?.length > 0 && (
                    <Table
                        pagination={false}
                        style={{width: 300}}
                        size={'small'}
                        dataSource={resolvedPlan?.rivers || []}
                        rowKey={'id'}
                        columns={[
                            {
                                key: '1',
                                dataIndex: '2',
                                title: `Rivers: ${resolvedPlan?.type}`,
                                render: (text, record) => (
                                    <Typography.Link
                                        onClick={() => {
                                            dispatchProject({
                                                type: AppActions.SET_PROJECT_SELECTION,
                                                data: {
                                                    selection: {
                                                        ...selection,
                                                        [type]: {
                                                            ...selection[type],
                                                            river: {...record},
                                                        }
                                                    }
                                                }
                                            })
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            color: AppColor.mainColor,
                                            fontWeight: selection[type]?.river?.key === record?.key ? 'bold' : '',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {record?.river} - {record?.reach}  {selection[type]?.river?.key === record?.key ? <>&#x2022;</> : ''}
                                    </Typography.Link>
                                )
                            }
                        ]}
                    />
                )
            }
            {
                resolvedProject?.shapeFiles?.filter(x => x.hasSurveyData)?.length > 0 && (
                    <Table
                        pagination={false}
                        style={{minWidth: 300}}
                        size={'small'}
                        dataSource={resolvedProject?.shapeFiles?.filter(x => x.hasSurveyData) || []}
                        rowKey={'id'}
                        columns={[
                            {
                                key: '1',
                                dataIndex: '2',
                                title: `Survey data: ${selection[type]?.survey?.title}`,
                                render: (text, record) => (
                                    <Typography.Link
                                        onClick={() => {
                                            dispatchProject({
                                                type: AppActions.SET_PROJECT_SELECTION,
                                                data: {
                                                    selection: {
                                                        ...selection,
                                                        [type]: {
                                                            ...selection[type],
                                                            survey: {...record}
                                                        }
                                                    }
                                                }
                                            })
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            color: AppColor.mainColor,
                                            fontWeight: selection[type]?.survey?.id === record?.id ? 'bold' : '',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {record?.title}
                                    </Typography.Link>
                                )
                            }
                        ]}
                    />
                )
            }
        </div>
    )
}